import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, Contact, ContactFilter } from "../lib/api";

export default function QuickReview() {
  const [params] = useSearchParams();
  const filter: ContactFilter = {
    review_status: params.get("review_status") || "pending",
    country: params.get("country") || "",
    type: params.get("type") || "",
    has_email: (params.get("has_email") as "0" | "1" | "") || "",
    title_contains: params.get("title_contains") || "",
    limit: 50,
  };

  const [queue, setQueue] = useState<Contact[]>([]);
  const [i, setI] = useState(0);
  const [stats, setStats] = useState({ relevant: 0, not_relevant: 0, deleted: 0 });

  const loadMore = useCallback(async () => {
    const r = await api.list(filter);
    setQueue(prev => {
      const ids = new Set(prev.slice(i).map(c => c.id));
      const fresh = r.contacts.filter(c => !ids.has(c.id));
      return [...prev.slice(0, i), ...fresh];
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [params]);

  useEffect(() => { loadMore(); /* eslint-disable-next-line */ }, [params]);
  useEffect(() => { if (queue.length - i < 5) loadMore(); /* eslint-disable-next-line */ }, [i, queue.length]);

  const cur = queue[i];

  const decide = useCallback(async (action: "relevant" | "not_relevant" | "delete") => {
    if (!cur) return;
    try {
      if (action === "delete") {
        await api.delete(cur.id);
        setStats(s => ({ ...s, deleted: s.deleted + 1 }));
      } else {
        await api.update(cur.id, { review_status: action });
        setStats(s => ({ ...s, [action]: (s as any)[action] + 1 }));
      }
      setI(n => n + 1);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }, [cur]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = document.activeElement as HTMLElement | null;
      if (el && /^(input|textarea|select)$/i.test(el.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === "r" || k === "arrowright") decide("relevant");
      else if (k === "x" || k === "arrowleft") decide("not_relevant");
      else if (k === "delete" || k === "backspace" || k === "d") decide("delete");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [decide]);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Quick review</h1>
          <p className="mt-1 text-sm text-slate-500">
            Tinder-style triage. <kbd>R</kbd>/<kbd>→</kbd> relevant · <kbd>X</kbd>/<kbd>←</kbd> not relevant · <kbd>Del</kbd>/<kbd>D</kbd> delete.
            <span className="ml-3">Session: <strong className="text-emerald-700">{stats.relevant}</strong> relevant · <strong className="text-amber-700">{stats.not_relevant}</strong> not · <strong className="text-rose-700">{stats.deleted}</strong> deleted</span>
          </p>
        </div>
        <Link to="/outreach/contacts" className="btn-secondary">← Back to table</Link>
      </div>

      {!cur && (
        <div className="card p-12 text-center text-slate-500">
          No more contacts in this queue. <Link className="text-indigo-700 underline" to="/outreach/contacts">Open the table</Link>.
        </div>
      )}

      {cur && (
        <div className="card mx-auto max-w-2xl p-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Contact #{i + 1}</div>
              <h2 className="mt-1 text-2xl font-semibold tracking-tightish text-slate-900">{cur.name}</h2>
              {cur.title && <div className="mt-1 text-sm text-slate-600">{cur.title}</div>}
            </div>
            {cur.profile_url && (
              <a href={cur.profile_url} target="_blank" rel="noreferrer" className="chip bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">MWC profile ↗</a>
            )}
          </div>

          <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <Field label="Company"  value={cur.company || "—"} />
            <Field label="Country"  value={cur.country || "—"} />
            <Field label="Type"     value={cur.type || "—"} />
            <Field label="Source"   value={cur.source || "—"} />
            <Field label="Email"    value={cur.email || "—"} mono />
            <Field label="Phone"    value={cur.phone || "—"} mono />
          </dl>

          <div className="mt-6 flex gap-3">
            <button onClick={() => decide("not_relevant")} className="btn-secondary flex-1 !py-3">
              <kbd className="mr-2">X</kbd> Not relevant
            </button>
            <button onClick={() => decide("delete")} className="btn-danger flex-1 !py-3">
              <kbd className="mr-2">Del</kbd> Delete
            </button>
            <button onClick={() => decide("relevant")} className="btn-primary flex-1 !py-3">
              <kbd className="mr-2">R</kbd> Relevant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md bg-slate-50 p-3 ring-1 ring-slate-100">
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className={`mt-0.5 text-slate-900 ${mono ? "font-mono text-xs" : "text-sm"}`}>{value}</dd>
    </div>
  );
}
